import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import EmailHtml from 'assets/components/html-templates/email/EmailTemplate';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import RichTextInput from 'assets/components/inputs/richTextInput/RichTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import { getLocales } from 'assets/locales/Locale';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { first } from 'lodash';
import ContractRefundInvoiceEmailTemplate from 'models/core/contractRefundInvoiceEmailTemplate/Model.ContractRefundInvoiceEmailTemplate';
import ContractRefundInvoiceEmailTemplateTags from 'module/management/components/contractRefundInvoiceEmailTemplate/ContractRefundInvoiceEmailTemplateTags';
import { useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { useContractRefundInvoiceProvider } from '../Sales.ContractRefundInvoice.Hooks';

export default function ContractRefundInvoiceSendEmail() {
  const { lang } = getLocales();
  const { sendEmail, emailTemplates, selected, pageRouter } = useContractRefundInvoiceProvider();
  const contractRefundInvoiceCreatedEmailTemplate = first(emailTemplates);

  const form = useForm<Api.Sales.ContractRefundInvoiceEmail.SendEmail.IRequest>({
    default: {
      htmlBody: renderToString(
        EmailHtml({
          body: 'Enter custom message here.',
        })
      ),
      additionalRecipientEmails: [],
      carbonCopies: [],
    },
    validation: (data, errors) => {
      if (!data.senderEmail) errors.senderEmail = lang.mustNotBeEmpty;
      if (data.senderEmail && !isEmailDomain(data.senderEmail, 'goautoshield.com'))
        errors.senderEmail = lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com');
      if (!data.senderName) errors.senderName = lang.mustNotBeEmpty;
      if (!data.subject) errors.subject = lang.mustNotBeEmpty;
      if (!data.htmlBody) errors.htmlBody = lang.mustNotBeEmpty;
      if (
        !data.sendToOwner &&
        !data.sendToRepresentative &&
        !data.sendToDealersAccounting &&
        (!data.additionalRecipientEmails || data?.additionalRecipientEmails?.every((it) => !isEmail(it))) &&
        !data.recipientsWithRole?.length
      )
        errors.additionalRecipientEmails = lang.mustNotBeEmpty;
      if (data.carbonCopies?.length && data.carbonCopies?.some((it) => !isEmail(it)))
        errors.carbonCopies = lang.invalidEmailFormatRequires;
    },
    onSubmit: (data) => sendEmail(new ContractRefundInvoiceEmailTemplate(data)),
  });

  useEffect(() => {
    if (contractRefundInvoiceCreatedEmailTemplate) {
      form.update({ ...contractRefundInvoiceCreatedEmailTemplate });
    }
  }, [contractRefundInvoiceCreatedEmailTemplate]);

  return (
    <Modal
      subcomponents={ThemeModalDefault}
      header={lang.emailTemplate}
      onClose={() => pageRouter.updateParams({})}
      render={
        <FormWrapper>
          {!selected?.invoiceDocumentId && <p>*{lang.invoiceEmailWillHaveNoAttachment}</p>}
          <InputWrapper>
            <TextInput
              name="senderName"
              label={lang.senderName}
              value={form.data.senderName}
              error={form.errors.senderName}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="senderEmail"
              label={lang.senderEmail}
              value={form.data.senderEmail}
              error={form.errors.senderEmail}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
          </InputWrapper>
          <h2>{lang.sendTo}</h2>
          <CheckWrapper>
            <CheckWrapper>
              <CheckInput
                name="sendToOwner"
                label={lang.owner}
                class={inputStyles.check.switch}
                value={form.data.sendToOwner}
                onChange={form.update}
                htmlElementProps={{ title: form.data.sendToOwner ? lang.disable : lang.enable }}
              />
              <CheckInput
                name="sendToRepresentative"
                label={lang.representative}
                class={inputStyles.check.switch}
                value={form.data?.sendToRepresentative}
                onChange={form.update}
                htmlElementProps={{ title: form.data?.sendToRepresentative ? lang.disable : lang.enable }}
              />
              <CheckInput
                name="sendToDealersAccounting"
                label={lang.dealersAccounting}
                class={inputStyles.check.switch}
                value={form.data?.sendToDealersAccounting}
                onChange={form.update}
                htmlElementProps={{ title: form.data?.sendToDealersAccounting ? lang.disable : lang.enable }}
              />
            </CheckWrapper>
          </CheckWrapper>
          <h2>{lang.additionalRecipientEmails}</h2>
          <DataMap
            data={[...(form.data.additionalRecipientEmails || []), '']}
            render={({ data: email, index }) => (
              <TextInput
                key={index}
                name="email"
                label={lang.email}
                value={email}
                error={form.errors.additionalRecipientEmails}
                onChange={({ email }) => {
                  let additionalRecipientEmails = form?.data?.additionalRecipientEmails || [];
                  additionalRecipientEmails.splice(index, 1, email);
                  additionalRecipientEmails = additionalRecipientEmails?.filter((it) => it);
                  form.update({ additionalRecipientEmails });
                }}
                icon={<Icon class="if-quill-pen" />}
              />
            )}
          />
          <h2>{lang.carbonCopies}</h2>
          <DataMap
            data={[...(form.data.carbonCopies || []), '']}
            render={({ data: carbonCopy, index }) => (
              <TextInput
                key={index}
                name="carbonCopy"
                label={lang.email}
                value={carbonCopy}
                error={form.errors.carbonCopies}
                onChange={({ carbonCopy }) => {
                  let carbonCopies = form?.data?.carbonCopies || [];
                  carbonCopies.splice(index, 1, carbonCopy);
                  carbonCopies = carbonCopies?.filter((it) => it);
                  form.update({ carbonCopies });
                }}
                icon={<Icon class="if-quill-pen" />}
              />
            )}
          />
          <h2>{lang.email}</h2>
          <TextInput
            name="subject"
            label={lang.subject}
            value={form.data.subject}
            error={form.errors.subject}
            onChange={form.update}
            icon={<Icon class="if-quill-pen" />}
          />
          <RichTextInput
            name="htmlBody"
            label={lang.htmlBody}
            value={form.data.htmlBody}
            error={form.errors.htmlBody}
            onChange={form.update}
            icon={<Icon class="if-quill-pen" />}
            customActions={<ContractRefundInvoiceEmailTemplateTags />}
          />
        </FormWrapper>
      }
      footer={
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={pageRouter.updateParams}
            data={{}}
            media="fas-ban"
            render={lang.cancel}
            class={ThemeButtonCircle}
          />
          <Button onClick={form.submit} render={lang.sendEmail} class={ThemeButtonCircle} active />
        </div>
      }
    />
  );
}
